import React, { ChangeEvent, useMemo } from "react";

import { AppBar, Box, Typography } from "@material-ui/core";

import clsx from "clsx";
import * as R from "ramda";

import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import Address from "api/types/Address";
import BusinessHeader from "components/BusinessHeader";

import MenuGroupSelector from "components/MenuGroupSelector";
import Price from "components/Price";
import SelectorChip from "components/SelectorChip";
import MenuDrawer from "containers/MenuDrawer";
import OrderTypeDrawer from "containers/OrderTypeDrawer";
import MenuContext from "contexts/MenuContext";
import { AvailableOrderTypes } from "gql/types/globals";
import makeStyles from "hooks/makeStyles";
import useBusiness from "hooks/useBusiness";
import useCart from "hooks/useCart";
import useFormatAddress from "hooks/useFormatAddress";
import useLocalize from "hooks/useLocalize";
import useMenus from "hooks/useMenus";
import ClockNine from "icons/ClockNine";
import MapPinIcon from "icons/MapPinIcon";
import ShoppingBagIcon from "icons/ShoppingBagIcon";

import ReadyTimeMessage from "../../OrderTypeDrawer/ReadyTimeMessage";

import BottomDrawers from "../BottomDrawers";
import Menu from "../Menu";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexDirection: "row"
	},
	header: {
		elevation: 1,
		backgroundColor: "transparent",
		justifyContent: "center",
		color: theme.palette.text.primary,
		zIndex: theme.zIndex.drawer + 1,
		height: 96,
		padding: theme.spacing(2),
		paddingBottom: theme.spacing(2.5),
		boxShadow: "none"
	},
	selectors: {
		marginBottom: theme.spacing(3),
		paddingLeft: theme.spacing(3)
	},
	menusSelector: {
		marginRight: theme.spacing(2)
	},
	orderTypeSelector: {
		flex: 1,
		justifyContent: "space-between",
		fontStyle: "normal",
		fontWeight: "bold",
		fontSize: theme.spacing(2),
		lineHeight: theme.spacing(2),
		alignItems: "center",
		color: theme.palette.text.secondary,
		backgroundColor: "transparent",
		borderBottom: "3px solid #828282",
		borderRadius: "0"
	},
	orderTypeSelectorNotSelected: {
		flex: 1,
		justifyContent: "space-between",
		fontStyle: "normal",
		fontWeight: "normal",
		fontSize: theme.spacing(2),
		lineHeight: theme.spacing(2),
		alignItems: "center",
		color: theme.palette.text.secondary,
		backgroundColor: "transparent",
		borderBottom: "none",
		borderRadius: "0"
	},
	orderTypeSelectorLabel: {
		flex: 1,
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between",
		[theme.breakpoints.up(480)]: {
			display: "block"
		}
	},
	orderTypeSelectorLabelBody: {
		[theme.breakpoints.up(480)]: {
			paddingRight: theme.spacing(1)
		}
	},
	orderTypeSelectorLabelHint: {
		fontWeight: "normal"
	},
	menuContainer: {
		height: "100vh",
		display: "flex",
		flexDirection: "row",
		flex: 1,
		paddingTop: theme.businessHeaderHeight,
		margin: 0,
		padding: 0,
		overflow: "hidden",
		justifyContent: "center",
		paddingLeft: theme.spacing(1)
	},
	leftNavigation: {
		display: "flex",
		width: "233px"
	},
	centerNavigation: {
		display: "flex",
		flex: "1",
		flexDirection: "column",
		maxWidth: "904px",
		width: "calc(100vw - 233px)"
	},
	icon: {
		height: theme.spacing(2),
		marginRight: theme.spacing(0.5),
		verticalAlign: "middle"
	},
	pickupOrder: {
		marginTop: "19px",
		display: "flex",
		alignItems: "center"
	},
	pickupOrderAddress: {
		marginRight: "16px"
	},
	pickupOrderAddressText: {
		fontSize: "12px",
		textTransform: "uppercase"
	},
	pickupTime: {
		cursor: "pointer",
		fontSize: "12px",
		marginRight: "19px",
		textTransform: "uppercase"
	},
	pickupTimeIcon: {
		height: theme.spacing(2),
		width: theme.spacing(2),
		marginRight: theme.spacing(0.5),
		verticalAlign: "middle"
	},
	addressIsRequired: {
		display: "inline-block",
		color: "red"
	},
	deliveryPriceIcon: {
		height: theme.spacing(2),
		width: theme.spacing(2),
		marginRight: theme.spacing(0.5),
		verticalAlign: "text-bottom"
	},
	priceText: {
		fontSize: "12px",
		display: "inline-block"
	}
}));

const MenuScreenBodyDesktop = () => {
	const navigate = useNavigate();
	const params = useParams();
	const classes = useStyles();
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const { business } = useBusiness();

	const { formatAddressShort } = useFormatAddress();

	const { menus } = useMenus();

	const { orderType, setOrderType, getCustomerInfo } = useCart();

	const menu = (params.menuSlug && menus.find(x => x.slug === params.menuSlug)) || menus[0];

	const handleChange = useMemo(
		() => (event: ChangeEvent<{}> | null, value: string) => {
			const orderType = business.orderTypes.find(({ id }) => id === value);

			if (orderType) {
				setOrderType(orderType);
			}
		},
		[business.orderTypes, setOrderType]
	);

	const menuContext = useMemo(
		() => ({
			menu,
			menuGroupBySlug: R.fromPairs(menu.menuGroups.map(x => [x.slug, x])),
			menuItemById: R.fromPairs(R.flatten(menu.menuGroups.map(x => x.menuItems)).map(x => [x.id, x]))
		}),
		[menu]
	);

	const customer = getCustomerInfo();

	return (
		<>
			<MenuDrawer selectedMenuSlug={menu ? menu.slug : null} />
			<OrderTypeDrawer />

			<Box className={classes.root}>
				<MenuContext.Provider value={menuContext}>
					<BottomDrawers />
					<AppBar className={classes.header}>
						<BusinessHeader />
					</AppBar>
					<Box className={classes.menuContainer}>
						<Box className={classes.leftNavigation}>
							<MenuGroupSelector menuGroups={menu.menuGroups} />
						</Box>
						<Box className={classes.centerNavigation}>
							<Box component="div" className={classes.selectors}>
								{business.orderTypes.map(({ id, name, localeNames }) => (
									<SelectorChip
										key={`ordertype${id}`}
										className={orderType?.id === id ? classes.orderTypeSelector : classes.orderTypeSelectorNotSelected}
										classes={{ label: classes.orderTypeSelectorLabel }}
										label={
											<>
												<span className={classes.orderTypeSelectorLabelBody}>{localize(localeNames, name || "")}</span>
												{/* {orderType?.id === id && <ReadyTimeMessage />} */}
											</>
										}
										onClick={e => {
											handleChange(e, id);
											navigate("#order-type?");
										}}
									/>
								))}

								<Box className={classes.pickupOrder}>
									<Box className={classes.pickupOrderAddress}>
										<Typography
											className={clsx(
												classes.pickupOrderAddressText,
												orderType?.orderType !== AvailableOrderTypes.pickup && !customer.deliveryAddress
													? classes.addressIsRequired
													: ""
											)}
										>
											<MapPinIcon className={classes.icon} />
											{orderType?.orderType === AvailableOrderTypes.pickup
												? formatAddressShort(business.address as Address)
												: customer?.deliveryAddress
												? formatAddressShort(customer.deliveryAddress as Address)
												: t("deliveryAddressNotDefined")}
										</Typography>
									</Box>
									<Typography className={classes.pickupTime} onClick={() => navigate("#checkout:1")}>
										<ClockNine className={classes.pickupTimeIcon} />
										<ReadyTimeMessage isDesktop={true} />
									</Typography>
									{orderType?.orderType !== AvailableOrderTypes.pickup && orderType?.priceCents ? (
										<Typography className={classes.pickupTime} onClick={() => navigate("#checkout:1")}>
											<ShoppingBagIcon className={classes.deliveryPriceIcon} />
											<Price
												className={classes.priceText}
												priceCents={orderType?.priceCents}
												suffix={t("deliveryFee")}
											/>
										</Typography>
									) : null}
								</Box>
							</Box>
							<Menu />
						</Box>
					</Box>
				</MenuContext.Provider>
			</Box>
		</>
	);
};

export default MenuScreenBodyDesktop;
